<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IPaymentSystemsFields } from '@modules/payment-systems/types';
import type { IPendingWithdrawalsFormFields } from '@modules/pending-withdrawals/submodules/view/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import { useFormatDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { usePaymentSystems } from '@modules/payment-systems/composables';
import PendingWithdrawalAcceptButton from '@modules/pending-withdrawals/components/PendingWithdrawalAcceptButton/PendingWithdrawalAcceptButton.vue';
import PendingWithdrawalDenyButton from '@modules/pending-withdrawals/components/PendingWithdrawalDenyButton/PendingWithdrawalDenyButton.vue';
import {
  ADMIN_PENDING_WITHDRAWALS_LIST_ROUTE_NAME,
  PENDING_WITHDRAWALS_PERMISSIONS,
  TRANSLATION_GROUP_PENDING_WITHDRAWALS,
} from '@modules/pending-withdrawals/constants';
import { usePendingWithdrawalsItemQuery } from '@modules/pending-withdrawals/submodules/view/queries';
import { isString } from '@utils/guards';

const route = useRoute();
const router = useRouter();
const { getTranslation } = useI18n();

const id = computed(() => (isString(route.params.id) ? Number(route.params.id) : 0));

const { data: withdrawal, isLoading } = usePendingWithdrawalsItemQuery(id);
const { formatDate } = useFormatDate();

const paymentSystem = computed(() => withdrawal.value?.payment_system_name);
const values = computed<IPaymentSystemsFields>(() => withdrawal.value?.payment_system_details_json ?? {});

const {
  paymentSystemSchema,
} = usePaymentSystems(
  paymentSystem,
  undefined,
  ref(false),
  ref('default'),
  true,
  values,
);

const formSchema = computed<FormSchema<IPendingWithdrawalsFormFields, never, {
  country_code: {
    selectOptionValue: 'country_code';
    selectOptionLabel: 'name';
  };
}>>(() => ({
  merchant_name: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'FORM_MERCHANT_NAME').value,
    value: withdrawal.value?.merchant_name,
  },
  affiliate_id: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'FORM_AFFILIATE_ID').value,
    value: withdrawal.value?.affiliate_id,
    useGrouping: false,
    maxFractionDigits: 0,
  },
  amount: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'FORM_AMOUNT').value,
    value: withdrawal.value?.amount,
  },
  created_at: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'FORM_CREATED_AT').value,
    value: withdrawal.value?.created_at ? formatDate(withdrawal.value.created_at) : undefined,
  },
  creator_type: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'FORM_CREATOR_TYPE').value,
    value: withdrawal.value?.creator_type,
  },
  creator_id: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'FORM_CREATOR_ID').value,
    value: withdrawal.value?.creator_id,
    useGrouping: false,
    maxFractionDigits: 0,
  },
  creator_email: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'FORM_CREATOR_EMAIL').value,
    value: withdrawal.value?.creator_email,
  },
  payment_system_name: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'FORM_PAYMENT_SYSTEM_NAME').value,
    value: withdrawal.value?.payment_system_name,
  },
  ...paymentSystemSchema.value,
}));

function goBack(): void {
  router.back({
    name: ADMIN_PENDING_WITHDRAWALS_LIST_ROUTE_NAME,
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'PendingWithdrawalsViewPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'VIEW_HEADER')"
  >
    <VLoader
      :loading="isLoading"
      min-height="200px"
    >
      <VForm
        :schema="formSchema"
        readonly
      >
        <template #secondaryBtn>
          <PendingWithdrawalAcceptButton
            v-if="$hasPermission(PENDING_WITHDRAWALS_PERMISSIONS.EDIT)"
            :id="id"
            @confirmed="goBack"
          />
          <PendingWithdrawalDenyButton
            v-if="$hasPermission(PENDING_WITHDRAWALS_PERMISSIONS.EDIT)"
            :id="id"
            @denied="goBack"
          />
          <VBackButton
            :to="{
              name: ADMIN_PENDING_WITHDRAWALS_LIST_ROUTE_NAME,
            }"
            :label="$getTranslation(TRANSLATION_GROUP_PENDING_WITHDRAWALS, 'VIEW_BACK')"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
